import { CountCreate, type CountResult, type CountSheetRow } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ASSIST_STATE, type AssistState } from '../components/ClaudeButton';
import { api, ApiError, newIdempotencyKey } from '../api';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { StockNav } from '../components/StockNav';
import { Empty, ErrorBox, QueryView, SearchBox } from '../components/ui';
import { formatDate, formatStamp, parseTyped, todayIso } from '../format';
import { useDebounced, useDesktop, useLeaveGuard, useSearchParam } from '../hooks';
import { useMeId } from '../auth';
import { useLocale } from '../i18n';
import { useSite } from '../sites';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

/** "12", " 12.5 ", "2,5", "$1,250" -> a number (never negative); "" or junk -> null (what was typed stays on screen). */
export function parseNumber(raw: string): number | null {
  const n = parseTyped(raw);
  return n !== null && n >= 0 ? n : null;
}

/**
 * The count typed for one item: cases in the cooler, decimals for an open one (2.5 = two cases and half of one).
 * Null when blank or junk; junk or a negative is "cannot count" (see countIsInvalid).
 */
export function parseCount(raw: string | undefined): number | null {
  if (raw === undefined || raw.trim() === '') return null;
  // a comma is the decimal point here (2,5 on a Spanish keyboard is two and a half, never 25 or 2,500 cases)
  const t = raw.trim().replace(',', '.');
  if (!/^\d*\.?\d*$/.test(t) || t === '.') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 && n <= MAX_COUNT ? n : null;
}
/** No cooler holds this many of one thing: a scanned barcode or a slip of the finger, refused before it is saved. */
export const MAX_COUNT = 9999;
export const countIsInvalid = (raw: string | undefined): boolean => raw !== undefined && raw.trim() !== '' && parseCount(raw) === null;

/** The line the API wants: the whole cases sealed, the part of a case as what is left in the open one. */
export function toLine(itemId: string, raw: string | undefined): CountCreate['lines'][number] | null {
  const typed = parseCount(raw);
  if (typed === null) return null;
  // to the thousandth first: 2.9996 is 3 cases, never 2 sealed and a whole case "open" (the server refuses that)
  const n = Math.round(typed * 1000) / 1000;
  const sealed = Math.floor(n + 1e-9);
  const part = Math.round((n - sealed) * 1000) / 1000;
  return part > 0 ? { itemId, sealed, open: { remaining: part, unit: 'unit' } } : { itemId, sealed };
}

/**
 * Where the count typed and not saved yet is kept on the device (decision 165): whose it is, the day it was typed, and
 * the lines per site. Only the person who typed it gets it back; a count from another day is offered, never put back by
 * itself (saved later it would set on hand to that day's numbers); what Claude filled is not kept (a person types it).
 */
export const COUNT_KEPT_KEY = 'morales.count.typed';
type Sites = Record<string, Record<string, string>>;
/** today's lines, and a count of another day still offered (kept beside them until the person decides) */
type Older = { day: string; sites: Sites };
type Kept = { userId: string; day: string; sites: Sites; older?: Older | null };
const isDay = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
const isSites = (v: unknown): v is Sites =>
  !!v && typeof v === 'object' && !Array.isArray(v) &&
  Object.values(v).every((lines) => !!lines && typeof lines === 'object' && !Array.isArray(lines) && Object.values(lines).every((x) => typeof x === 'string'));
export function readKeptCount(): Kept | null {
  try {
    const raw = localStorage.getItem(COUNT_KEPT_KEY);
    const v = raw ? (JSON.parse(raw) as Partial<Kept> | null) : null;
    if (!v || typeof v !== 'object' || typeof v.userId !== 'string' || !isDay(v.day) || !isSites(v.sites)) return null;
    const older = v.older && isDay(v.older.day) && isSites(v.older.sites) ? v.older : null;
    return { userId: v.userId, day: v.day, sites: v.sites, older };
  } catch {
    return null;
  }
}
const lineCount = (sites: Sites) => Object.values(sites).reduce((n, l) => n + Object.keys(l).length, 0);
const dropEmpty = (sites: Sites): Sites => Object.fromEntries(Object.entries(sites).filter(([, lines]) => Object.keys(lines).length > 0));
function writeKept(v: Kept | null) {
  try {
    const sites = v ? dropEmpty(v.sites) : {};
    const older = v?.older && lineCount(v.older.sites) > 0 ? { ...v.older, sites: dropEmpty(v.older.sites) } : null;
    if (v && (Object.keys(sites).length || older)) localStorage.setItem(COUNT_KEPT_KEY, JSON.stringify({ ...v, sites, older }));
    else localStorage.removeItem(COUNT_KEPT_KEY);
  } catch {
    // storage blocked (a private window): the count still works, only not kept across a reload
  }
}
const without = (sites: Sites, site: string, itemIds: Set<string>): Sites => ({ ...sites, [site]: Object.fromEntries(Object.entries(sites[site] ?? {}).filter(([id]) => !itemIds.has(id))) });
/** Take saved lines out of what is kept (today's and an older day's offered), whether the screen is still open or not. */
function forgetKept(site: string, itemIds: Set<string>) {
  const k = readKeptCount();
  if (!k) return;
  writeKept({ ...k, sites: without(k.sites, site, itemIds), older: k.older ? { ...k.older, sites: without(k.older.sites, site, itemIds) } : null });
}
/** what is on screen wins over what comes back from the phone */
const mergeSites = (back: Sites, cur: Sites): Sites =>
  Object.fromEntries([...new Set([...Object.keys(back), ...Object.keys(cur)])].map((site) => [site, { ...(back[site] ?? {}), ...(cur[site] ?? {}) }]));

export function StockPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [q, setQ] = useSearchParam('q');
  const term = useDebounced(q.trim().toLowerCase());
  // what is typed belongs to the site it was typed for: switching site shows (and saves) that site's own
  // kept on this phone until saved (decision 165): a reload in the cooler, a lost signal or a closed tab loses nothing
  const meId = useMeId();
  const today = todayIso();
  const [typedBySite, setTypedBySite] = useState<Sites>({});
  /** a count typed on another day by this person: offered with its day, kept or dropped by them */
  const [older, setOlder] = useState<Older | null>(null);
  /** what Claude filled and nobody has touched yet: not kept on the phone */
  const [fromClaude, setFromClaude] = useState<Set<string>>(new Set());
  // once the person is known: their own count of today comes back, one of another day is offered
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!meId || ready) return;
    const k = readKeptCount();
    if (k && k.userId === meId) {
      if (k.day === today) {
        setTypedBySite((cur) => mergeSites(k.sites, cur));
        setOlder(k.older ?? null);
      } else if (lineCount(k.sites) > 0) setOlder({ day: k.day, sites: k.sites });
    }
    setReady(true);
  }, [meId, ready]);
  // today's lines are kept as they are typed, even while an older day's count is still offered (kept beside them)
  useEffect(() => {
    if (!ready || !meId) return;
    const sites = Object.fromEntries(Object.entries(typedBySite).map(([site, lines]) => [site, Object.fromEntries(Object.entries(lines).filter(([id]) => !fromClaude.has(`${site}:${id}`)))]));
    // a shared phone: another person's kept count is left alone until this person has something of their own to keep
    const stored = readKeptCount();
    if (stored && stored.userId !== meId && lineCount(sites) === 0 && !older) return;
    writeKept({ userId: meId, day: today, sites, older });
  }, [typedBySite, fromClaude, meId, older, ready, today]);
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const [saved, setSaved] = useState<CountResult | null>(null);
  // Same discipline as the order form: one Idempotency-Key per payload, kept across network retries.
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const desktop = useDesktop();
  const { siteId } = useSite();
  const siteKey = siteId ?? '';
  const typed = typedBySite[siteKey] ?? {};
  // the Claude button (decision 137) opens the count filled in for this site: the person checks it and saves
  const location = useLocation();
  const navigate = useNavigate();
  const [claudeFilled, setClaudeFilled] = useState(false);
  const filledFrom = useRef<string | null>(null);
  useEffect(() => {
    const action = (location.state as Partial<AssistState> | null)?.[ASSIST_STATE];
    if (!action || action.kind !== 'count') return;
    // once per arrival (React may run this twice in development)
    if (filledFrom.current === location.key) return;
    filledFrom.current = location.key;
    setTypedBySite((all) => ({ ...all, [siteKey]: { ...(all[siteKey] ?? {}), ...Object.fromEntries(action.lines.map((l) => [l.itemId, String(l.count)])) } }));
    setFromClaude((cur) => new Set([...cur, ...action.lines.map((l) => `${siteKey}:${l.itemId}`)]));
    setClaudeFilled(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.key]);
  const sheet = useQuery({ queryKey: qk.countSheet(siteId ?? ''), queryFn: ({ signal }) => api.countSheet(siteId, signal) });

  const save = useMutation({
    mutationFn: (v: { body: CountCreate; key: string; site: string }) => api.saveCount(v.body, v.key),
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: (result, v) => {
      attempt.current = null;
      const sent = new Set(v.body.lines.map((l) => l.itemId));
      // saved: never offered again, even when the screen was left before the answer came
      forgetKept(v.site, sent);
      if (!mounted.current) return;
      setTypedBySite((all) => without(all, v.site, sent));
      setOlder((o) => (o ? { ...o, sites: without(o.sites, v.site, sent) } : o));
      setSaved(result);
      void qc.invalidateQueries({ queryKey: ['stock'] });
    },
    onError: (error) => {
      if (!(error instanceof ApiError && error.status === 0)) attempt.current = null;
    },
  });

  // only products on the sheet: a kept line for a product archived since is left out, never blocks the save
  const onSheet = new Set((sheet.data?.rows ?? []).map((r) => r.itemId));
  const lines = Object.entries(typed).flatMap(([itemId, raw]) => {
    if (sheet.data && !onSheet.has(itemId)) return [];
    const line = toLine(itemId, raw);
    return line ? [line] : [];
  });
  const invalid = Object.entries(typed).filter(([itemId, raw]) => !(sheet.data && !onSheet.has(itemId)) && countIsInvalid(raw)).length;
  // a kept line for a product off the sheet now (archived since) is dropped, from the screen and the phone
  useEffect(() => {
    if (!sheet.data) return;
    const ids = new Set(sheet.data.rows.map((r) => r.itemId));
    const off = (lines: Record<string, string> | undefined) => Object.keys(lines ?? {}).filter((id) => !ids.has(id));
    if (off(typedBySite[siteKey]).length) setTypedBySite((all) => without(all, siteKey, new Set(off(all[siteKey]))));
    if (older && off(older.sites[siteKey]).length) setOlder((o) => (o ? { ...o, sites: without(o.sites, siteKey, new Set(off(o.sites[siteKey]))) } : o));
  }, [sheet.data, siteKey, older, typedBySite]);

  const olderCount = older ? lineCount(older.sites) : 0;
  const olderBanner = older && olderCount > 0 ? (
    <div className="warn-band" role="alert" data-testid="count-older">
      {t('countOlder').replace('{n}', String(olderCount)).replace('{day}', formatDate(older.day, intlLocale))}{' '}
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setTypedBySite((cur) => mergeSites(older.sites, cur)); setOlder(null); }}>{t('countOlderKeep')}</button>{' '}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOlder(null)}>{t('countOlderDrop')}</button>
    </div>
  ) : null;

  function submit() {
    if (invalid > 0) return setProblem('countInvalid');
    if (lines.length === 0) return setProblem('nothingToSave');
    const parsed = CountCreate.safeParse({ ...(siteId ? { siteId } : {}), lines });
    if (!parsed.success) return setProblem('error');
    setProblem(null);
    setSaved(null);
    const serialized = JSON.stringify(parsed.data);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    save.mutate({ body: parsed.data, key: attempt.current.key, site: siteKey });
  }

  const edit = (itemId: string, raw: string) => {
    setProblem(null);
    setTypedBySite((all) => ({ ...all, [siteKey]: { ...(all[siteKey] ?? {}), [itemId]: raw } }));
    // a person typed it: it is theirs now, kept like the rest
    setFromClaude((cur) => (cur.has(`${siteKey}:${itemId}`) ? new Set([...cur].filter((k) => k !== `${siteKey}:${itemId}`)) : cur));
  };

  const visible = (rows: CountSheetRow[]) => (term ? rows.filter((r) => r.itemName.toLowerCase().includes(term)) : rows);
  const lastCounted = (r: CountSheetRow) => (r.lastCountedAt ? formatStamp(r.lastCountedAt, intlLocale, 'date') : t('neverCounted'));
  // counting is done on a phone: typed counts are kept from a tab tapped by mistake there too
  useLeaveGuard(lines.length > 0, t('countUnsaved'));
  // what was missed or found stays in the stock ledger (the adjustments); the count screen only says it was saved
  // far from what the ledger expected: named to count again, without the numbers (decision 130)
  const recheck = saved?.lines.filter((l) => l.flagged).map((l) => l.itemName) ?? [];
  const claudeNote = claudeFilled && lines.length > 0 ? (
    <p className="flash claude-filled" role="status">
      {t('claudeFilled')}
    </p>
  ) : null;
  const savedNote = saved ? (
    <div className="flash" role="status">
      {t('countSaved')}: {saved.counted}
      {recheck.length > 0 ? ` · ${t('countAgain')}: ${recheck.join(', ')}` : ''}
    </div>
  ) : null;

  if (desktop) {
    type Row = CountSheetRow & { raw: string | undefined };
    const rows: Row[] = visible(sheet.data?.rows ?? []).map((r) => ({ ...r, raw: typed[r.itemId] }));
    const columns: SheetColumn<Row>[] = [
      { key: 'item', header: t('itemCol'), render: (r) => r.itemName },
      { key: 'unit', header: t('countUnitCol'), hint: t('countUnitHint'), width: '70px', render: (r) => r.packUnit },
      {
        key: 'last',
        header: t('lastCount'),
        width: '160px',
        render: (r) => lastCounted(r),
        cellClass: (r) => (r.lastCountedAt ? undefined : 'src-pattern'),
      },
      {
        key: 'count',
        header: t('coolerCount'),
        hint: t('coolerCountHint'),
        width: '120px',
        align: 'right',
        render: (r) => r.raw ?? '',
        edit: { kind: 'number', get: (r) => r.raw ?? '', set: (r, v) => ({ ...r, raw: v.trim() }) },
        canEdit: () => !save.isPending,
        cellClass: (r) => (r.raw === undefined || r.raw === '' ? undefined : countIsInvalid(r.raw) ? 'unsaved bad' : 'unsaved'),
      },
    ];
    return (
      <div className="desk-page">
        <header className="desk-head">
          <div>
            <h1>{t('countTitle')}</h1>
            <p className="muted">{t('countGridHint')}</p>
          </div>
        </header>
        <StockNav />
        <div className="toolbar">
          <input className="tool-search" type="search" placeholder={t('findItem')} aria-label={t('findItem')} value={q} onChange={(e) => setQ(e.target.value)} />
          <span className="spacer" />
          <button type="button" className="btn btn-primary btn-sm" onClick={submit} disabled={save.isPending || lines.length === 0 || invalid > 0}>
            {save.isPending ? t('sending') : t('saveCountN').replace('{n}', String(lines.length))}
          </button>
        </div>
        {claudeNote}
        {savedNote}
      {lines.length > 0 ? <p className="note" data-testid="count-kept">{t('countKept').replace('{n}', String(lines.length))}</p> : null}
      {olderBanner}
        {problem ? <p className="field-error" role="alert">{t(problem)}</p> : null}
        {save.error ? <ErrorBox error={save.error} /> : null}
        <QueryView query={sheet}>
          {() =>
            rows.length === 0 ? (
              <p className="muted pick-first">{t('noResults')}</p>
            ) : (
              <div style={{ maxWidth: 720 }}>
                <Sheet
                  label={t('countTitle')}
                  columns={columns}
                  rows={rows}
                  rowKey={(r) => r.itemId}
                  rowLabel={(r) => r.itemName}
                  onRowsChange={(next, at) => {
                    const r = next[at.row]!;
                    edit(r.itemId, r.raw ?? '');
                  }}
                />
              </div>
            )
          }
        </QueryView>
      </div>
    );
  }

  return (
    <div className="stack">
      <section className="stack-sm">
        <h1>{t('countTitle')}</h1>
        <StockNav />
        <p className="muted">{t('countHint')}</p>
      </section>
      {claudeNote}
      {savedNote}
      {lines.length > 0 ? <p className="note" data-testid="count-kept">{t('countKept').replace('{n}', String(lines.length))}</p> : null}
      {olderBanner}
      {problem ? <p className="field-error" role="alert">{t(problem)}</p> : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
      <SearchBox value={q} onChange={setQ} />
      <QueryView query={sheet}>
        {(data) => {
          const rows = visible(data.rows);
          return rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {rows.map((r) => {
                const raw = typed[r.itemId];
                const bad = countIsInvalid(raw);
                return (
                  <li key={r.itemId}>
                    <div className="row count-head">
                      <span className="row-main">
                        <span className="row-title">{r.itemName}</span>
                        <span className="row-sub">
                          {t('lastCount')}: {lastCounted(r)}
                        </span>
                      </span>
                      <label className="count-field">
                        <span className="label">
                          {t('coolerCount')} ({r.packUnit})
                        </span>
                        <input
                          className={bad ? 'count-input bad' : raw ? 'count-input filled' : 'count-input'}
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="—"
                          aria-label={`${t('coolerCount')}: ${r.itemName}`}
                          aria-invalid={bad || undefined}
                          value={raw ?? ''}
                          onChange={(e) => edit(r.itemId, e.target.value)}
                        />
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>
          );
        }}
      </QueryView>
      {lines.length > 0 || invalid > 0 ? (
        <div className="actionbar">
          <div className="total">
            <span>{t('cooler')}</span>
            <strong>
              {lines.length} {t('toSave')}
            </strong>
            {invalid > 0 ? <span className="field-error">{t('countInvalid')}</span> : null}
          </div>
          <button type="button" className="btn btn-primary btn-lg" onClick={submit} disabled={save.isPending}>
            {save.isPending ? t('sending') : t('save')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
